import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getWorkspaces } from 'shared/store/selectors/workspace.selector';
import { generateBreadcrumbs } from 'shared/store/selectors/knowledge.selector';
import { buildKnowledgeLink } from 'src/helpers/knowledge-link.helper';

const PATH_SEPARATOR = ',';

/**
 * For a SOURCE category, returns the list of accessible top-level mirrors of it
 * across workspaces — used to render the "Mirrored in:" badge tooltip.
 *
 * Each target carries:
 *   - workspaceName: the workspace where the mirror lives
 *   - pathTitles: titles from workspace root down to (and including) the mirror
 *   - link: relative URL to navigate to the mirror
 *
 * Top-level = the mirror's immediate parent isn't itself a mirror (the user-
 * initiated mirror root, not an auto-created descendant inside an existing
 * mirror subtree). Mirrors in workspaces the viewer can't access aren't in
 * `categoriesById` to begin with, so the privacy gate is implicit.
 *
 * Targets are sorted alphabetically by workspaceName.
 *
 * @param {string} sourceCategoryId
 * @returns {Array<{ workspaceName: string, pathTitles: string[], link: string }>}
 */
export function useMirroredInTargets(sourceCategoryId) {
  const categoriesById = useSelector(state => state.knowledge?.categoriesById);
  const categories = useSelector(state => state.knowledge?.categories);
  const workspaces = useSelector(getWorkspaces);

  return useMemo(() => {
    if (!sourceCategoryId || !categoriesById) return [];

    const workspaceNameById = new Map((workspaces ?? []).map(w => [w.id, w.name]));

    const mirrorsOfSource = Object.values(categoriesById).filter(
      c => c?.mirroredFrom && c.mirroredFrom === sourceCategoryId,
    );

    const topLevelMirrors = mirrorsOfSource.filter(m => {
      const parentId = (m.path || '').split(PATH_SEPARATOR).filter(Boolean).pop();
      const parent = parentId ? categoriesById[parentId] : null;
      return !parent?.mirroredFrom;
    });

    const buildTarget = mirror => {
      const workspaceName = workspaceNameById.get(mirror.workspaceId) || '';
      const pathParts = (mirror.path || '').split(PATH_SEPARATOR);
      const ancestorCategories = generateBreadcrumbs(
        { categoriesById, categories },
        { pathParts, initialWorkspaceId: mirror.workspaceId },
      );
      const pathTitles = [
        ...ancestorCategories.map(c => c?.title || ''),
        mirror.title,
      ];
      const link = buildKnowledgeLink({
        fullPath: mirror.fullPath,
        workspaceId: mirror.workspaceId,
        type: mirror.type,
      });
      return { workspaceName, pathTitles, link };
    };

    return topLevelMirrors
      .map(buildTarget)
      .sort((a, b) => a.workspaceName.localeCompare(b.workspaceName));
  }, [sourceCategoryId, categoriesById, categories, workspaces]);
}
